       // Utility function to prevent duplicate initialization and handle DOMContentLoaded timing
  export interface InitOnceOptions {
               // When true, clears the one-shot flag and reruns initialization on bfcache
                // restore, since DOMContentLoaded won't fire again. Defaults to false to
                // preserve existing callers' one-shot behavior.
                resetOnPersistedNavigation?: boolean;
            }

  // Tracks which flags already have a pageshow listener registered, so calling
  // initOnceWithDOMReady repeatedly for the same flagName (e.g. re-triggered dynamic imports)
  // never attaches duplicate listeners.
  const persistedNavigationResetRegistered = new Set<string>();

  function registerPersistedNavigationReset(key: string, run: (isPersistedReset: boolean) => void): void {
                if (persistedNavigationResetRegistered.has(key)) return;
                persistedNavigationResetRegistered.add(key);

                window.addEventListener('pageshow', (event: PageTransitionEvent) => {
                    if (!event.persisted) return;
                    (globalThis as any)[key] = false;
                    run(true);
                });
            }

  export function initOnceWithDOMReady(
                flagName: string,
                // True only for bfcache-triggered reinitialization. Allows modules to refresh
                // stale content without repeating one-time side effects like auto-printing.
                // Existing callers can ignore this optional parameter.
                initializer: (isPersistedReset?: boolean) => void,
                options?: InitOnceOptions
            ) {
                const key = `__${flagName}Init`;

                const run = (isPersistedReset = false) => {
                    if ((globalThis as any)[key]) {
                        console.log(`${flagName} already initialized — skipping.`);
                        return;
                    }

                (globalThis as any)[key] = true;

                // Handle DOMContentLoaded timing properly
                if (document.readyState === 'loading') {
                    // DOM is still loading, wait for DOMContentLoaded
                    document.addEventListener('DOMContentLoaded', () => {
                        initializer(isPersistedReset);
                        console.log(`${flagName} initialized (waited for DOMContentLoaded)`);
                    }, { once: true });
                } else {
                    // DOM is already loaded, initialize immediately
                    initializer(isPersistedReset);
                    console.log(`${flagName} initialized (DOM already ready)`);
                }
            }
                run(false);

                if (options?.resetOnPersistedNavigation) {
                    registerPersistedNavigationReset(key, run);
                }
            }

  // Tracks flagNames that already have an "every show" pageshow listener registered, so
  // calling initOnEveryShow repeatedly for the same flagName never attaches duplicate
  // listeners (same dedupe approach as registerPersistedNavigationReset above).
  const everyShowRegistered = new Set<string>();

/**
 * Runs the initializer when the DOM is ready and on every subsequent pageshow.
 * Use when a module needs to refresh on every page restore, regardless of the
 * bfcache persisted flag.
 *
 * Unlike initOnceWithDOMReady, this has no permanent initialization guard.
 * The initializer must be safe to run repeatedly without duplicating listeners
 * or creating new singletons.
 *
 * @param flagName Unique name for logging and pageshow-listener de-duplication.
 * @param initializer Receives false on initial run and true on subsequent pageshow events.
 */
  export function initOnEveryShow(flagName: string, initializer: (isPersistedReset?: boolean) => void): void {
                const run = (isPersistedReset = false) => {
                    initializer(isPersistedReset);
                    console.log(`${flagName} ${isPersistedReset ? 're-initialized (pageshow)' : 'initialized'}`);
                };
                // Handle DOMContentLoaded timing properly for the very first run, exactly like
                // initOnceWithDOMReady, so a module dynamically imported after DOMContentLoaded
                // has already fired still initializes correctly.
                if (document.readyState === 'loading') {
                    document.addEventListener('DOMContentLoaded', () => run(false), { once: true });
                } else {
                    run(false);
                }

                if (everyShowRegistered.has(flagName)) return;
                everyShowRegistered.add(flagName);

                let hasHandledInitialPageShow = false;
                window.addEventListener('pageshow', () => {
                    // Every page fires exactly one 'pageshow' following its very first load, even
                    // without bfcache involved. That first occurrence corresponds to the run already
                    // performed above, so it's skipped once to avoid an immediate duplicate run.
                    if (!hasHandledInitialPageShow) {
                        hasHandledInitialPageShow = true;
                        return;
                    }
                    run(true);
                });
            }